import {DateTime} from 'luxon';
import {PRESET_RULES, RelativeDatePreset} from "./presets";


export type RelativeDateUnit = 'day' | 'week' | 'month';

export type RelativeDateAnchor =
    | { type: 'now' }
    | { type: 'date'; value: DateTime };

export type RelativeDateRule =
    | {
    kind: 'bounded';
    direction: 'last' | 'next' | 'current';
    amount?: number;
    unit: RelativeDateUnit;
    anchor?: RelativeDateAnchor;
}
    | {
    kind: 'open';
    direction: 'before' | 'after';
    amount: number;
    unit: RelativeDateUnit;
    anchor?: RelativeDateAnchor;
};

export type DateInterval = {
    start: DateTime | null;
    end: DateTime | null;
};


export function resolveAnchor(anchor?: RelativeDateAnchor): DateTime {
    if (!anchor || anchor.type === 'now') {
        return DateTime.utc();
    }
    return anchor.value;
}

export function assertValidAmount(rule: RelativeDateRule): void {
    if (rule.kind === 'bounded' && rule.direction === 'current') {
        return;
    }

    if (!Number.isInteger(rule.amount) || (rule.amount ?? 0) < 1) {
        throw new Error(
            `Relative date rule amount must be a positive integer, got ${rule.amount}`,
        );
    }
}

export function resolveBoundedDayInterval(
    direction: 'last' | 'next' | 'current',
    amount: number,
    anchor: DateTime,
): DateInterval {
    if (direction === 'current') {
        return {
            start: anchor.startOf('day'),
            end: anchor.endOf('day'),
        };
    }

    if (direction === 'last') {
        return {
            start: anchor.startOf('day').minus({day: amount}),
            end: anchor.endOf('day'),
        };
    }

    return {
        start: anchor.startOf('day'),
        end: anchor.endOf('day').plus({day: amount}),
    };
}

export function resolveBoundedSnappedInterval(
    direction: 'last' | 'next' | 'current',
    amount: number,
    unit: 'week' | 'month',
    anchor: DateTime,
): DateInterval {
    if (direction === 'current') {
        return {
            start: anchor.startOf(unit),
            end: anchor.endOf(unit),
        };
    }

    if (direction === 'last') {
        const intervalEnd = anchor.startOf(unit).minus({millisecond: 1});
        return {
            start: intervalEnd.minus({[unit]: amount - 1}).startOf(unit),
            end: intervalEnd,
        };
    }

    const intervalStart = anchor.startOf(unit).plus({[unit]: 1});
    return {
        start: intervalStart,
        end: intervalStart.plus({[unit]: amount - 1}).endOf(unit),
    };
}

export function resolveOpenInterval(
    direction: 'before' | 'after',
    amount: number,
    unit: RelativeDateUnit,
    anchor: DateTime,
): DateInterval {
    const pivot = anchor.plus({[unit]: direction === 'before' ? -amount : amount});
    if (direction === 'before') {
        return {
            start: null,
            end: pivot.endOf(unit),
        };
    }
    return {
        start: pivot.startOf(unit),
        end: null,
    };
}

export function resolveDateInterval(rule: RelativeDateRule): DateInterval {
    assertValidAmount(rule);
    const anchor = resolveAnchor(rule.anchor);

    if (rule.kind === 'open') {
        return resolveOpenInterval(rule.direction, rule.amount, rule.unit, anchor);
    }

    if (rule.unit === 'day') {
        return resolveBoundedDayInterval(rule.direction, rule.amount ?? 1, anchor);
    }

    return resolveBoundedSnappedInterval(
        rule.direction,
        rule.amount ?? 1,
        rule.unit,
        anchor,
    );
}

export function presetToRule(preset: RelativeDatePreset): RelativeDateRule {
    return PRESET_RULES[preset];
}

export function resolvePresetInterval(preset: RelativeDatePreset): DateInterval {
    return resolveDateInterval(presetToRule(preset));
}
